import { Component, EventEmitter, Input, Output } from '@angular/core';
import { HoverIdComponent } from '@clinic-platform/ui-components';

import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { StatusBadgeComponent } from '../../../shared/ui/status-badge/status-badge.component';
import { StatusBadgeViewModel } from '../../../shared/ui/status-badge/status-badge.models';
import { AppointmentListItem } from '../data-access/appointments.models';
import { AppointmentActionsComponent } from './appointment-actions.component';

@Component({
  selector: 'app-appointments-table',
  standalone: true,
  imports: [
    TranslatePipe,
    HoverIdComponent,
    StatusBadgeComponent,
    AppointmentActionsComponent
  ],
  template: `
    <div class="appointments-table">
      <table class="appointments-table__table">
        <thead>
          <tr>
            <th>{{ 'Clinic' | t }}</th>
            @if (!isClientActor) {
              <th>{{ 'Client' | t }}</th>
            }
            <th>{{ 'Specialist' | t }}</th>
            <th>{{ 'Procedure' | t }}</th>
            <th>{{ 'Device' | t }}</th>
            <th>{{ 'Session Time' | t }}</th>
            <th>{{ 'common.status' | t }}</th>
            <th class="appointments-table__col appointments-table__col--confirmation">{{ 'Confirmation Required' | t }}</th>
            <th class="appointments-table__col appointments-table__col--confirmed-at">{{ 'Confirmed At' | t }}</th>
            <th>{{ 'common.actions' | t }}</th>
          </tr>
        </thead>

        <tbody>
          @for (item of items; track item.id) {
            <tr>
              <td>
                <lib-hover-id [value]="hoverId(item.clinicId)">
                  @if (canOpenClinic && item.clinicId.trim()) {
                    <button
                      type="button"
                      class="appointments-table__link-button"
                      (click)="clinicOpenClicked.emit(item.clinicId)"
                      [title]="clinicLabel(item)"
                    >
                      {{ clinicLabel(item) }}
                    </button>
                  } @else {
                    <span [title]="clinicLabel(item)">{{ clinicLabel(item) }}</span>
                  }
                </lib-hover-id>
              </td>
              @if (!isClientActor) {
                <td>
                  <lib-hover-id [value]="hoverId(item.clientId)">
                    @if (canOpenClient && item.clientId.trim()) {
                      <button
                        type="button"
                        class="appointments-table__link-button"
                        (click)="clientOpenClicked.emit(item.clientId)"
                        [title]="clientTitle(item)"
                      >
                        {{ clientLabel(item) }}
                      </button>
                    } @else {
                      <span [title]="clientTitle(item)">{{ clientLabel(item) }}</span>
                    }
                  </lib-hover-id>
                </td>
              }
              <td>
                <lib-hover-id [value]="hoverId(item.specialistUserId)">
                  @if (canOpenSpecialist && item.specialistUserId?.trim()) {
                    <button
                      type="button"
                      class="appointments-table__link-button"
                      (click)="specialistOpenClicked.emit(item.specialistUserId ?? '')"
                      [title]="specialistLabel(item)"
                    >
                      {{ specialistLabel(item) }}
                    </button>
                  } @else {
                    <span [title]="specialistLabel(item)">{{ specialistLabel(item) }}</span>
                  }
                </lib-hover-id>
              </td>
              <td>
                <lib-hover-id [value]="hoverId(item.procedureTypeId)">
                  @if (canOpenProcedure && item.procedureTypeId.trim()) {
                    <button
                      type="button"
                      class="appointments-table__link-button"
                      (click)="procedureOpenClicked.emit(item.procedureTypeId)"
                      [title]="procedureLabel(item)"
                    >
                      {{ procedureLabel(item) }}
                    </button>
                  } @else {
                    <span [title]="procedureLabel(item)">{{ procedureLabel(item) }}</span>
                  }
                </lib-hover-id>
              </td>
              <td>
                <lib-hover-id [value]="hoverId(item.deviceId)">
                  @if (canOpenDevice && item.deviceId?.trim()) {
                    <button
                      type="button"
                      class="appointments-table__link-button"
                      (click)="deviceOpenClicked.emit(item.deviceId ?? '')"
                      [title]="deviceLabel(item)"
                    >
                      {{ deviceLabel(item) }}
                    </button>
                  } @else {
                    <span [title]="deviceLabel(item)">{{ deviceLabel(item) }}</span>
                  }
                </lib-hover-id>
              </td>
              <td>
                <lib-hover-id [value]="hoverId(item.primarySlotId)">
                  <span [title]="sessionTimeLabel(item)">{{ sessionTimeLabel(item) }}</span>
                </lib-hover-id>
              </td>
              <td>
                <app-status-badge
                  [label]="getStatusViewModel(item.status).label"
                  [tone]="getStatusViewModel(item.status).tone"
                />
              </td>
              <td class="appointments-table__col appointments-table__col--confirmation">
                {{ item.confirmationRequired ? ('common.yes' | t) : ('common.no' | t) }}
              </td>
              <td class="appointments-table__col appointments-table__col--confirmed-at">
                {{ formatAsSchedulingDateTime(item.confirmedAt, item) }}
              </td>
              <td>
                <app-appointment-actions
                  [item]="item"
                  [isBusy]="busyAppointmentId === item.id"
                  [canConfirmOrCancel]="canConfirmOrCancel"
                  [canComplete]="canComplete"
                  [isClientActor]="isClientActor"
                  (editClicked)="editClicked.emit(item)"
                  (checkInClicked)="checkInClicked.emit(item)"
                  (confirmClicked)="confirmClicked.emit(item)"
                  (cancelClicked)="cancelClicked.emit(item)"
                  (completeClicked)="completeClicked.emit(item)"
                  (noShowClicked)="noShowClicked.emit(item)"
                />
              </td>
            </tr>
          }
        </tbody>
      </table>

      <div class="appointments-table__cards">
        @for (item of items; track item.id) {
          <section class="appointments-table__card">
            <div class="appointments-table__card-head">
              <lib-hover-id [value]="hoverId(item.procedureTypeId)">
                @if (canOpenProcedure && item.procedureTypeId.trim()) {
                  <button
                    type="button"
                    class="appointments-table__link-button appointments-table__link-button--card appointments-table__card-title-button"
                    (click)="procedureOpenClicked.emit(item.procedureTypeId)"
                    [title]="procedureLabel(item)"
                  >
                    {{ procedureLabel(item) }}
                  </button>
                } @else {
                  <div class="appointments-table__card-title">{{ procedureLabel(item) }}</div>
                }
              </lib-hover-id>
              <app-status-badge
                [label]="getStatusViewModel(item.status).label"
                [tone]="getStatusViewModel(item.status).tone"
              />
            </div>

            <div class="appointments-table__card-grid">
              <div class="appointments-table__card-row">
                <div class="appointments-table__card-label">{{ 'Clinic' | t }}</div>
                <div class="appointments-table__card-value">
                  <lib-hover-id [value]="hoverId(item.clinicId)">
                    @if (canOpenClinic && item.clinicId.trim()) {
                      <button
                        type="button"
                        class="appointments-table__link-button appointments-table__link-button--card"
                        (click)="clinicOpenClicked.emit(item.clinicId)"
                        [title]="clinicLabel(item)"
                      >
                        {{ clinicLabel(item) }}
                      </button>
                    } @else {
                      <span>{{ clinicLabel(item) }}</span>
                    }
                  </lib-hover-id>
                </div>
              </div>

              @if (!isClientActor) {
                <div class="appointments-table__card-row">
                  <div class="appointments-table__card-label">{{ 'Client' | t }}</div>
                  <div class="appointments-table__card-value">
                    <lib-hover-id [value]="hoverId(item.clientId)">
                      @if (canOpenClient && item.clientId.trim()) {
                        <button
                          type="button"
                          class="appointments-table__link-button appointments-table__link-button--card"
                          (click)="clientOpenClicked.emit(item.clientId)"
                          [title]="clientTitle(item)"
                        >
                          {{ clientLabel(item) }}
                        </button>
                      } @else {
                        <span [title]="clientTitle(item)">{{ clientLabel(item) }}</span>
                      }
                    </lib-hover-id>
                  </div>
                </div>
              }

              <div class="appointments-table__card-row">
                <div class="appointments-table__card-label">{{ 'Specialist' | t }}</div>
                <div class="appointments-table__card-value">
                  <lib-hover-id [value]="hoverId(item.specialistUserId)">
                    @if (canOpenSpecialist && item.specialistUserId?.trim()) {
                      <button
                        type="button"
                        class="appointments-table__link-button appointments-table__link-button--card"
                        (click)="specialistOpenClicked.emit(item.specialistUserId ?? '')"
                        [title]="specialistLabel(item)"
                      >
                        {{ specialistLabel(item) }}
                      </button>
                    } @else {
                      <span>{{ specialistLabel(item) }}</span>
                    }
                  </lib-hover-id>
                </div>
              </div>

              <div class="appointments-table__card-row">
                <div class="appointments-table__card-label">{{ 'Device' | t }}</div>
                <div class="appointments-table__card-value">
                  <lib-hover-id [value]="hoverId(item.deviceId)">
                    @if (canOpenDevice && item.deviceId?.trim()) {
                      <button
                        type="button"
                        class="appointments-table__link-button appointments-table__link-button--card"
                        (click)="deviceOpenClicked.emit(item.deviceId ?? '')"
                        [title]="deviceLabel(item)"
                      >
                        {{ deviceLabel(item) }}
                      </button>
                    } @else {
                      <span>{{ deviceLabel(item) }}</span>
                    }
                  </lib-hover-id>
                </div>
              </div>

              <div class="appointments-table__card-row">
                <div class="appointments-table__card-label">{{ 'Session Time' | t }}</div>
                <div class="appointments-table__card-value">
                  <lib-hover-id [value]="hoverId(item.primarySlotId)">
                    <span>{{ sessionTimeLabel(item) }}</span>
                  </lib-hover-id>
                </div>
              </div>

              <div class="appointments-table__card-row">
                <div class="appointments-table__card-label">{{ 'Confirmation Required' | t }}</div>
                <div class="appointments-table__card-value">
                  {{ item.confirmationRequired ? ('common.yes' | t) : ('common.no' | t) }}
                </div>
              </div>

              @if (item.confirmedAt) {
                <div class="appointments-table__card-row">
                  <div class="appointments-table__card-label">{{ 'Confirmed At' | t }}</div>
                  <div class="appointments-table__card-value">{{ formatAsSchedulingDateTime(item.confirmedAt, item) }}</div>
                </div>
              }
            </div>

            <div class="appointments-table__card-actions">
              <app-appointment-actions
                [item]="item"
                [isBusy]="busyAppointmentId === item.id"
                [canConfirmOrCancel]="canConfirmOrCancel"
                [canComplete]="canComplete"
                [isClientActor]="isClientActor"
                (editClicked)="editClicked.emit(item)"
                (checkInClicked)="checkInClicked.emit(item)"
                (confirmClicked)="confirmClicked.emit(item)"
                (cancelClicked)="cancelClicked.emit(item)"
                (completeClicked)="completeClicked.emit(item)"
                (noShowClicked)="noShowClicked.emit(item)"
              />
            </div>
          </section>
        }
      </div>
    </div>
  `,
  styles: [`
    .appointments-table {
      background: #ffffff;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
    }

    .appointments-table__table {
      width: 100%;
      border-collapse: collapse;
      min-width: 1300px;
    }

    .appointments-table__cards {
      display: none;
    }

    .appointments-table__table th,
    .appointments-table__table td {
      text-align: left;
      padding: 12px 14px;
      border-bottom: 1px solid #f3f4f6;
      vertical-align: middle;
    }

    .appointments-table__table th {
      background: #f9fafb;
      color: #374151;
      font-size: 13px;
    }

    .appointments-table__table td {
      color: #111827;
      font-size: 13px;
    }

    .appointments-table__link-button {
      all: unset;
      cursor: pointer;
      color: #2563eb;
      text-decoration: underline;
      text-underline-offset: 2px;
      display: inline-block;
      max-width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    @media (max-width: 900px) {
      .appointments-table__table {
        min-width: 760px;
      }

      .appointments-table__col--confirmed-at {
        display: none;
      }

    }

    @media (max-width: 640px) {
      .appointments-table {
        border: none;
        background: transparent;
      }

      .appointments-table__table {
        display: none;
      }

      .appointments-table__cards {
        display: grid;
        gap: 12px;
      }

      .appointments-table__card {
        border: 1px solid #e5e7eb;
        border-radius: 14px;
        background: #ffffff;
        padding: 12px;
        box-shadow: 0 6px 18px rgba(15, 23, 42, 0.05);
      }

      .appointments-table__card-head {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 10px;
        margin-bottom: 10px;
      }

      .appointments-table__card-title {
        min-width: 0;
        font-size: 14px;
        font-weight: 700;
        color: #111827;
      }

      .appointments-table__card-title-button {
        font-size: 14px;
        font-weight: 700;
      }

      .appointments-table__card-grid {
        display: grid;
        gap: 10px;
      }

      .appointments-table__card-row {
        display: grid;
        grid-template-columns: minmax(88px, 108px) minmax(0, 1fr);
        gap: 10px;
        align-items: start;
      }

      .appointments-table__card-label {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: #64748b;
      }

      .appointments-table__card-value {
        min-width: 0;
        font-size: 13px;
        color: #111827;
        word-break: break-word;
      }

      .appointments-table__card-actions {
        margin-top: 12px;
        display: flex;
        justify-content: flex-end;
      }

      .appointments-table__link-button {
        white-space: normal;
        overflow: visible;
        text-overflow: clip;
      }

      .appointments-table__link-button--card {
        text-align: left;
      }
    }
  `]
})
export class AppointmentsTableComponent
{
  @Input()
  public items: AppointmentListItem[] = [];

  @Input()
  public busyAppointmentId: string | null = null;

  @Input()
  public canConfirmOrCancel = true;

  @Input()
  public canComplete = true;

  @Input()
  public isClientActor = false;

  @Input()
  public canOpenClient = false;

  @Input()
  public canOpenDevice = false;

  @Input()
  public canOpenClinic = false;

  @Input()
  public canOpenSpecialist = false;

  @Input()
  public canOpenProcedure = false;

  @Output()
  public readonly editClicked = new EventEmitter<AppointmentListItem>();

  @Output()
  public readonly checkInClicked = new EventEmitter<AppointmentListItem>();

  @Output()
  public readonly confirmClicked = new EventEmitter<AppointmentListItem>();

  @Output()
  public readonly cancelClicked = new EventEmitter<AppointmentListItem>();

  @Output()
  public readonly completeClicked = new EventEmitter<AppointmentListItem>();

  @Output()
  public readonly noShowClicked = new EventEmitter<AppointmentListItem>();

  @Output()
  public readonly clientOpenClicked = new EventEmitter<string>();

  @Output()
  public readonly deviceOpenClicked = new EventEmitter<string>();

  @Output()
  public readonly clinicOpenClicked = new EventEmitter<string>();

  @Output()
  public readonly specialistOpenClicked = new EventEmitter<string>();

  @Output()
  public readonly procedureOpenClicked = new EventEmitter<string>();

  protected getStatusViewModel(status: AppointmentListItem['status']): StatusBadgeViewModel
  {
    switch (status)
    {
      case 'pending_confirmation':
        return { label: 'status.pendingConfirmation', tone: 'warning' };

      case 'confirmed':
        return { label: 'status.confirmed', tone: 'success' };

      case 'completed':
        return { label: 'status.completed', tone: 'info' };

      case 'cancelled':
        return { label: 'status.cancelled', tone: 'neutral' };

      case 'no_show':
        return { label: 'status.noShow', tone: 'danger' };

      case 'reschedule_required':
        return { label: 'status.rescheduleRequired', tone: 'danger' };

      default:
        return { label: status, tone: 'neutral' };
    }
  }

  protected readableId(value: string | null | undefined): string
  {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return '—';
    }

    if (this.isUuidLike(normalized)) {
      return `${normalized.slice(0, 8)}...${normalized.slice(-6)}`;
    }

    if (normalized.length > 26) {
      return `${normalized.slice(0, 16)}...${normalized.slice(-6)}`;
    }

    return normalized;
  }

  protected showIdMeta(value: string | null | undefined): boolean
  {
    const normalized = value?.trim() ?? '';
    return this.isUuidLike(normalized) || normalized.length > 26;
  }

  protected hoverId(value: string | null | undefined): string | null
  {
    return this.showIdMeta(value) ? (value?.trim() ?? null) : null;
  }

  protected clinicLabel(item: AppointmentListItem): string
  {
    const fromItem = item.clinicLabel?.trim();
    if (fromItem) {
      return fromItem;
    }

    return this.readableId(item.clinicId);
  }

  protected clientLabel(item: AppointmentListItem): string
  {
    return item.clientLabel?.trim() || this.readableId(item.clientId);
  }

  protected clientTitle(item: AppointmentListItem): string
  {
    return item.clientLabel?.trim() || item.clientId;
  }

  protected procedureLabel(item: AppointmentListItem): string
  {
    return item.procedureTypeLabel?.trim() || this.readableId(item.procedureTypeId);
  }

  protected specialistLabel(item: AppointmentListItem): string
  {
    return item.specialistLabel?.trim() || this.readableId(item.specialistUserId);
  }

  protected deviceLabel(item: AppointmentListItem): string
  {
    const fromItem = item.deviceLabel?.trim();
    if (fromItem) {
      return fromItem;
    }

    return this.readableId(item.deviceId);
  }

  protected sessionTimeLabel(item: AppointmentListItem): string
  {
    if (item.appointmentStartAt && item.appointmentEndAt) {
      return `${this.formatAsSchedulingDate(item.appointmentStartAt, item)} ${this.formatAsSchedulingTime(item.appointmentStartAt, item)} -> ${this.formatAsSchedulingTime(item.appointmentEndAt, item)}`;
    }

    if (item.primarySlotStartAt && item.primarySlotEndAt) {
      return `${this.formatAsSchedulingDate(item.primarySlotStartAt, item)} ${this.formatAsSchedulingTime(item.primarySlotStartAt, item)} -> ${this.formatAsSchedulingTime(item.primarySlotEndAt, item)}`;
    }

    return this.readableId(item.primarySlotId);
  }

  protected formatAsSchedulingDateTime(
    value: string | null | undefined,
    item: AppointmentListItem,
  ): string
  {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return '—';
    }

    const parsed = new Date(normalized);
    if (Number.isNaN(parsed.getTime())) {
      return normalized;
    }

    const timeZone = item.clinicTimeZone?.trim() || undefined;
    const dateText = new Intl.DateTimeFormat(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone,
    }).format(parsed);
    const timeText = new Intl.DateTimeFormat(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone,
    }).format(parsed);
    return `${dateText} ${timeText}`;
  }

  private formatAsSchedulingDate(value: string, item: AppointmentListItem): string {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      const directMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      return directMatch ? `${directMatch[3]}.${directMatch[2]}.${directMatch[1]}` : value;
    }

    return new Intl.DateTimeFormat(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: item.clinicTimeZone?.trim() || undefined,
    }).format(parsed);
  }

  private formatAsSchedulingTime(value: string, item: AppointmentListItem): string {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      const directMatch = value.match(/(?:T|\s)(\d{2}):(\d{2})/);
      return directMatch ? `${directMatch[1]}:${directMatch[2]}` : value;
    }

    return new Intl.DateTimeFormat(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: item.clinicTimeZone?.trim() || undefined,
    }).format(parsed);
  }

  private isUuidLike(value: string): boolean
  {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
  }
}
